import React, { useState, useEffect } from "react";
import AccountSidebar from "../../components/AccountSidebar";
import { toast } from "react-hot-toast";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import axiosInstance from "../../../axiosInstance";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import getDecryptData from "../../../helper/getDecryptData";
import LoadingScreen from "../../components/LoadingScreen";
import eraseCookie from "../../../helper/eraseCookie";

const Accountprofile = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [SubmitLoading, setSubmitLoading] = useState(true);

  // dropdown data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [inputs, setInputs] = useState({
    type: "",
    username: "",
    phone: "",
    email: "",
    password: "",
    confirm_password: "",
    pincode: "",
    Gender: "1",
    DOB: "",
    address: "",

    // new location fields
    country: "",
    countryName: "",
    state: "",
    stateName: "",
    city: "",
    cityName: "",

    SetEmail: "",
  });

  const capitalizeEachWord = (str) => {
    if (!str) return str;
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const CreateSlug = (str) => {
    let slug = str || "";
    slug = slug.toLowerCase();
    slug = slug.replace(/\s+/g, "-");
    slug = slug.replace(/[^a-z0-9\-]/g, "");
    return slug;
  };

  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // ------------------ LOAD DROPDOWNS ------------------
  const fetchCountries = async () => {
    try {
      const response = await axiosInstance.get("/countries");
      setCountries(response.data.countries || []);
    } catch (error) {
      console.error("Error fetching countries:", error);
      toast.error("Error fetching countries");
    }
  };

  const fetchStatesByCountry = async (countryId) => {
    try {
      if (!countryId) {
        setStates([]);
        return;
      }
      const response = await axiosInstance.get(`/states/${countryId}`);
      setStates(response.data.states || []);
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Error fetching states");
    }
  };

  const fetchCitiesByState = async (stateId) => {
    try {
      if (!stateId) {
        setCities([]);
        return;
      }
      const response = await axiosInstance.get(`/cities/${stateId}`);
      setCities(response.data.cities || []);
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Error fetching cities");
    }
  };

  // ------------------ USER DATA ------------------
  const fetchUserById = async () => {
    const decryptdatajson = await getDecryptData();
    const id = decryptdatajson?._id;

    if (!id) {
      toast.error("Please Login First");
      navigate("/login");
      return;
    }

    try {
      const { data } = await axiosInstance.post("/auth-user", { id });
      const { success, existingUser } = data;

      if (success) {
        setInputs((prevData) => ({
          ...prevData,
          username: existingUser.username || "",
          phone: existingUser.phone || "",
          email: existingUser.email || "",
          address: existingUser.address || "",
          pincode: existingUser.pincode || "",
          state: existingUser.state || "",
          stateName: existingUser.stateName || existingUser.statename || "",
          country: existingUser.country || "",
          countryName: existingUser.countryName || "",
          city: existingUser.city || "",
          cityName: existingUser.cityName || "",
          about: existingUser.about || "",
          SetEmail: existingUser.email || "",
          Gender: existingUser.Gender || "1",
        }));

        // preload dependent dropdowns
        if (existingUser.country) {
          await fetchStatesByCountry(existingUser.country);
        }
        if (existingUser.state) {
          await fetchCitiesByState(existingUser.state);
        }
      }
    } catch (error) {
      console.error("Error during user fetch:", error);
      toast.error(error?.response?.data?.message || "Error fetching profile");
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchCountries();
      await fetchUserById();
      setLoading(false);
    };
    init();
  }, []);

  // ------------------ HANDLE CHANGE ------------------
  const handleChange = async (e) => {
    const { name, value, type, checked, files } = e.target;
    let newValue;

    if (type === "checkbox") {
      newValue = checked;
    } else if (type === "file") {
      newValue = files[0];
    } else {
      newValue = value;
    }

    // country changed
    if (name === "country") {
      const selectedCountry = countries.find((item) => item._id === value);

      setInputs((prevData) => ({
        ...prevData,
        country: value,
        countryName: selectedCountry?.name || "",
        state: "",
        stateName: "",
        city: "",
        cityName: "",
      }));

      setStates([]);
      setCities([]);
      await fetchStatesByCountry(value);
      return;
    }

    // state changed
    if (name === "state") {
      const selectedState = states.find((item) => item._id === value);

      setInputs((prevData) => ({
        ...prevData,
        state: value,
        stateName: selectedState?.name || "",
        city: "",
        cityName: "",
      }));

      setCities([]);
      await fetchCitiesByState(value);
      return;
    }

    // city changed
    if (name === "city") {
      const selectedCity = cities.find((item) => item._id === value);

      setInputs((prevData) => ({
        ...prevData,
        city: value,
        cityName: selectedCity?.name || "",
      }));
      return;
    }

    if (name === "username") {
      newValue = capitalizeEachWord(newValue);
      setInputs((prevData) => ({
        ...prevData,
        profile_url: CreateSlug(newValue),
      }));
    }

    if (name === "profile_url") {
      newValue = CreateSlug(newValue);
    }

    setInputs((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));
  };

  // ------------------ SUBMIT ------------------
  const handleSubmit = async () => {
    const decryptdatajson = await getDecryptData();
    const id = decryptdatajson?._id;

    if (!id) {
      toast.error("Please Login First");
      navigate("/login");
      setSubmitLoading(true);
      return;
    }

    setSubmitLoading(false);

    const fields = [
      { name: "username", message: "Please enter Full Name" },
      { name: "phone", message: "Please enter phone number" },
      { name: "email", message: "Please enter email" },
      { name: "country", message: "Please select country" },
      { name: "state", message: "Please select state" },
      { name: "city", message: "Please select city" },
      { name: "pincode", message: "Please enter pincode" },
      { name: "address", message: "Please enter address" },
    ];

    for (const field of fields) {
      if (!inputs[field.name]) {
        toast.error(field.message);
        setSubmitLoading(true);
        return;
      }
    }

    try {
      const { data } = await axiosInstance.put(`/admin/update-user-details/${id}`, inputs);
      const { success } = data;

      if (success) {
        if (inputs.SetEmail === "") {
          toast.success("Profile Updated Successfully | Please login");
          navigate("/login");
          eraseCookie("token");
        } else {
          toast.success("Profile Updated Successfully");
        }
      }
    } catch (error) {
      console.error("Error On Update:", error);
      toast.error(error?.response?.data?.message || "Profile update failed");
    } finally {
      setSubmitLoading(true);
    }
  };

  return (
    <>
      <Header />

      <Helmet>
        <title>Account Profile | {window.location.hostname}</title>
      </Helmet>

      <div className="user-dasboard whitesmoke">
        <div className="container pt-4">
          <div className="row pb-4">
            <AccountSidebar />

            <div className="col-lg-9 my-lg-0 my-1">
              <div id="main-content" className="bg-white border">
                <h4 className="mb-2">My Profile</h4>

                {loading ? (
                  <LoadingScreen />
                ) : (
                  <form className="m-b20 row">
                    <div className="card border-0 bg-white p-4 mb-4">
                      <div className="row">
                        <div className="mb-4">
                          <label className="form-label text-dark" htmlFor="username">
                            Full Name <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <input
                              type="text"
                              id="username"
                              className="form-control"
                              name="username"
                              value={inputs.username}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div className="mb-4 col-6">
                          <label className="form-label text-dark" htmlFor="email">
                            Email <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <input
                              readOnly={inputs.SetEmail !== ""}
                              type="email"
                              id="email"
                              className="form-control"
                              name="email"
                              value={inputs.email}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div className="mb-4 col-6">
                          <label className="form-label text-dark" htmlFor="phone">
                            Phone <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <input
                              readOnly
                              type="text"
                              id="phone"
                              className="form-control"
                              name="phone"
                              value={inputs.phone}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div className="mb-4 col-6">
                          <label className="form-label text-dark" htmlFor="Gender">
                            Gender <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <select
                              className="form-control"
                              value={inputs.Gender}
                              name="Gender"
                              onChange={handleChange}
                            >
                              <option value="" disabled>
                                Select Gender
                              </option>
                              <option value="1">Male</option>
                              <option value="2">Female</option>
                              <option value="0">Other</option>
                            </select>
                          </div>
                        </div>

                        <div className="mb-4 col-6">
                          <label className="form-label text-dark" htmlFor="country">
                            Country <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <select
                              className="form-control"
                              id="country"
                              name="country"
                              value={inputs.country}
                              onChange={handleChange}
                            >
                              <option value="" disabled>
                                Select Country
                              </option>
                              {countries.map((item) => (
                                <option key={item._id} value={item._id}>
                                  {item.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mb-4 col-6">
                          <label className="form-label text-dark" htmlFor="state">
                            State <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <select
                              className="form-control"
                              id="state"
                              name="state"
                              value={inputs.state}
                              onChange={handleChange}
                              disabled={!inputs.country}
                            >
                              <option value="" disabled>
                                Select State
                              </option>
                              {states.map((item) => (
                                <option key={item._id} value={item._id}>
                                  {item.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mb-4 col-6">
                          <label className="form-label text-dark" htmlFor="city">
                            City <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <select
                              className="form-control"
                              id="city"
                              name="city"
                              value={inputs.city}
                              onChange={handleChange}
                              disabled={!inputs.state}
                            >
                              <option value="" disabled>
                                Select City
                              </option>
                              {cities.map((item) => (
                                <option key={item._id} value={item._id}>
                                  {item.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mb-4 col-6">
                          <label className="form-label text-dark" htmlFor="pincode">
                            Pincode <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <input
                              type="number"
                              id="pincode"
                              className="form-control custom-date"
                              name="pincode"
                              value={inputs.pincode}
                              onChange={handleChange}
                            />
                          </div>
                        </div>

                        <div className="mb-4 col-12">
                          <label className="form-label text-dark" htmlFor="address">
                            Address <span className="text-danger">( Required )</span>
                          </label>
                          <div className="input-group input-sm">
                            <textarea
                              id="address"
                              className="form-control custom-date"
                              name="address"
                              value={inputs.address}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-12 text-end">
                      {SubmitLoading ? (
                        <button className="btn btn-primary ms-2" type="button" onClick={handleSubmit}>
                          Update Profile
                        </button>
                      ) : (
                        <button disabled className="btn btn-primary ms-2" type="button">
                          <span className="ms-1">Loading...</span>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Accountprofile;
